/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Colors, Grid } from '@coursera/cds-core';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import HelpDropdownGridItem from 'bundles/composable-page-header/components/HelpDropdownGridItem';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import MobileWebChatTriggerGridItem from 'bundles/composable-page-header/components/grid-items/MobileWebChatTriggerGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileNavBarLogo from 'bundles/composable-page-header/components/grid-items/home-logo/MobileNavBarLogo';
import MobileAccountMenuGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileAccountMenuGridItem';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import AccountMenuConfig from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuConfig';
import AccountSubMenu from 'bundles/composable-page-header/components/mobile-menu-items/AccountSubMenu';
import useMobileNavIconRowGapStyle from 'bundles/composable-page-header/hooks/useMobileNavIconRowGapStyle';
import SimpleMobileMenuConfig from 'bundles/composable-page-header/module-renderers/SimpleMobileMenuConfig';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';

type Props = {
  customHeaderLogos?: Array<React.ReactNode>;
  acctDropdownLogoutOnly?: boolean;
  enableCourseraLogoLink?: boolean;
  showProgramSwitcher?: boolean;
  showLanguageDropdown?: boolean;
  showHelpDropdown?: boolean;
  showAcctDropdown?: boolean;
  showAcctItemsInMain?: boolean;
  showSignupBtn?: boolean;
  showLoginBtn?: boolean;
  showMobileHamburger?: boolean;
  additionalRightItems?: React.ReactNode;
};

const styles = {
  gridItemContainer: css`
    display: flex;
    align-items: center;
  `,
};

const LoggedInConsumerSimpleMobileMainNav = ({
  customHeaderLogos = undefined,
  enableCourseraLogoLink = true,
  showProgramSwitcher = false,
  showLanguageDropdown = true,
  showHelpDropdown = true,
  acctDropdownLogoutOnly = false,
  showAcctDropdown = true,
  showAcctItemsInMain = false,
  showSignupBtn = true,
  showLoginBtn = true,
  showMobileHamburger = true,
  additionalRightItems,
}: Props) => {
  const {
    enrolledDegreePrograms,
    chosenDegree,
    isCourseraPlusLearner,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
  } = useConsumerMyPageHeader();

  const isOrgScoped = useIsOrgScopedHome();
  const mobileNavIconRowGapStyle = useMobileNavIconRowGapStyle();

  const experienceSwitcherProps = {
    currentOrganization: naptimeChosenOrganization,
    currentDegree: chosenDegree,
    degrees: enrolledDegreePrograms,
    organizations: naptimeOrganizationsWithProgramEnrollments,
  };

  const homeLogoHref = enableCourseraLogoLink ? '/' : undefined;

  const defaultLogo = (
    <CourseraHomeLogo key="coursera-home-logo" logoColor={!enableCourseraLogoLink ? Colors.gray[900] : undefined} />
  );
  const logos = customHeaderLogos?.length ? customHeaderLogos : [defaultLogo];

  const drawerLogo = <HomeLogoGridItemModule href={homeLogoHref} logos={logos} />;
  const navBarLogo = <MobileNavBarLogo href={homeLogoHref} logos={logos} />;

  return (
    <MainNavigationWrapper>
      <Grid item container alignItems="center" justifyContent="space-between">
        <Grid item xs={8} sm={6} container alignItems="center">
          {showMobileHamburger && !isOrgScoped && (
            <MobileMenuIconGridItem
              menuConfig={
                <SimpleMobileMenuConfig
                  acctDropdownLogoutOnly={acctDropdownLogoutOnly}
                  showProgramSwitcher={showProgramSwitcher}
                  showLanguageDropdown={showLanguageDropdown}
                  showAcctDropdown={showAcctDropdown}
                  showAcctItemsInMain={showAcctItemsInMain}
                  showSignupBtn={showSignupBtn}
                  showLoginBtn={showLoginBtn}
                />
              }
              homeLogo={drawerLogo}
            />
          )}
          {navBarLogo}
        </Grid>
        <Grid item css={[styles.gridItemContainer, mobileNavIconRowGapStyle]}>
          <MobileWebChatTriggerGridItem />
          {showHelpDropdown && <HelpDropdownGridItem disableChatSsr={true} />}
          {isOrgScoped && (
            <>
              {showAcctDropdown && (
                <MobileAccountMenuGridItem
                  isCourseraPlusLearner={Boolean(isCourseraPlusLearner)}
                  menuConfig={
                    <AccountMenuConfig
                      naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                      canViewEducatorAdminLink={canViewEducatorAdminLink}
                      hasCheatingIncidents={hasCheatingIncidents}
                      {...(showProgramSwitcher && experienceSwitcherProps)}
                    />
                  }
                />
              )}

              {acctDropdownLogoutOnly && (
                <MobileAccountMenuGridItem
                  isCourseraPlusLearner={Boolean(isCourseraPlusLearner)}
                  menuConfig={<AccountSubMenu dropdownLinks={[]} />}
                  hideFooter
                />
              )}
            </>
          )}
          {additionalRightItems}
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default LoggedInConsumerSimpleMobileMainNav;
